// =============================================================================
// RequestDetailBanners.jsx
// =============================================================================
// The three transient banners shown above the request body:
//   1. Save confirmation toast — appears bottom-right when `savedMsg` is set.
//   2. Staged decisions banner — for staff/responders, shown when approvals
//      have been picked but not yet confirmed-and-sent.
//   3. Draft banner — for shelter users on a Draft request, with Continue
//      Editing and Delete Draft buttons.
//
// Pulled out of RequestDetail.jsx as part of the Phase D extraction pass.
//
// Props
// ─────
//   savedMsg         — string — toast text; empty/falsy hides the toast
//   isStaff          — staff/responder vs shelter (gates the staged + draft banners)
//   req              — full request object (read for status/refNo/approvalsConfirmedAt/id)
//   onContinueEdit   — handler called with `req` when the user clicks Continue Editing
//                      on a Draft (also gates whether the draft banner renders at all)
//   onDeleteDraft    — optional handler called with `req.id` after a successful
//                      delete; if absent, we fall back to onBack()
//   onBack           — fallback navigation after deleting a draft
// =============================================================================
import React from "react";
import { updateRequestById } from "../../data/requests";
import { confirmDialog } from "../../services/dialogService";
import { logAudit } from "../../utils/auditLog";
import { REQUEST_STATUSES } from "../../utils/enums";
import { Btn } from "../../utils/ui.jsx";

export function RequestDetailBanners({
  savedMsg,
  isStaff,
  req,
  onContinueEdit,
  onDeleteDraft,
  onBack,
}) {
  return (
    <React.Fragment>
      {/* Save confirmation toast */}
      {savedMsg && (
        <div
          // animation is not a Tailwind default — keep inline for that property only
          style={{ animation: "fadeIn 0.2s ease" }}
          className="fixed bottom-6 right-6 z-[9999] bg-zephyr-teal-dark text-white py-3 px-5 rounded-[10px] text-sm font-semibold shadow-[0_4px_16px_rgba(0,0,0,0.2)]"
        >
          {savedMsg}
        </div>
      )}

      {/* Staged decisions banner — for responders */}
      {isStaff &&
        req.approvalsConfirmedAt === null &&
        (req.status === REQUEST_STATUSES.PART_PROCESSED ||
          req.status === REQUEST_STATUSES.FULLY_PROCESSED) && (
          <div className="bg-zephyr-gold-light border border-zephyr-gold rounded-[10px] py-3 px-5 mb-4 flex justify-between items-center gap-3">
            <div className="text-[13px] text-[#7c4a00]">
              <strong>Decisions staged — not yet confirmed.</strong> Open the Approval Decisions
              panel and click Confirm & Send to Shelter when ready.
            </div>
          </div>
        )}

      {/* Draft banner */}
      {!isStaff &&
        (req.status === REQUEST_STATUSES.DRAFT || req.refNo?.startsWith("DRAFT")) &&
        onContinueEdit && (
          <div className="bg-zephyr-gold-light border border-zephyr-gold rounded-[10px] py-[14px] px-5 mb-4 flex justify-between items-center gap-3">
            <div className="text-sm text-[#7c4a00]">
              <strong>This request is saved as a draft.</strong> It has not been submitted yet {"—"}{" "}
              continue editing to make changes or upload files.
            </div>
            <div className="flex gap-[10px] shrink-0">
              <Btn
                onClick={() => onContinueEdit(req)}
                style={{ background: "#00897B", color: "white", whiteSpace: "nowrap" }}
              >
                Continue Editing →
              </Btn>
              <Btn
                variant="secondary"
                onClick={async function () {
                  if (
                    !(await confirmDialog({
                      title: "Delete draft?",
                      message: "This draft will be removed from your list.",
                      confirmLabel: "Delete",
                      danger: true,
                    }))
                  )
                    return;
                  // Soft-delete (decision D-5): keep the row, flag it deactivated.
                  // requests_view filters out deactivated rows, so it disappears
                  // from every list but is retained in the DB for traceability.
                  updateRequestById(req.id, { deactivated: true }).then(function (res) {
                    if (!res.error) {
                      // Audit the deletion so a draft's lifecycle is traceable
                      // (created -> deleted vs submitted).
                      logAudit("draft_deleted", {
                        request_id: req.id,
                        ref: req.refNo,
                        shelter: req.shelterCode,
                      });
                      if (onDeleteDraft) onDeleteDraft(req.id);
                      else onBack();
                    }
                  });
                }}
                style={{
                  background: "none",
                  border: "1px solid #EF5350",
                  color: "#C62828",
                  whiteSpace: "nowrap",
                }}
              >
                Delete Draft
              </Btn>
            </div>
          </div>
        )}
    </React.Fragment>
  );
}
